<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { searchContacts } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ConversationTranscriptsSynthesisDialog from '../../ConversationTranscriptsSynthesisDialog.vue';

const props = defineProps({
  initialFrom: {
    type: Number,
    default: null,
  },
  initialTo: {
    type: Number,
    default: null,
  },
  isSynthesizing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['synthesize']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const synthesisDialogRef = ref(null);
/** When true, a stacked modal (questions) is open — disable outside-click close on the parent. */
const isSynthesisOverlayOpen = ref(false);
const synthesisFilterSnapshot = ref(null);
const initialFocusRef = ref(null);
const agentContactPair = ref(false);
const useDateRange = ref(true);
const dateRange = ref([new Date(), new Date()]);
const isDatePickerOpen = ref(false);
const selectedAgents = ref([]);
const pairAgent = ref(null);
const contactSearchQuery = ref('');
const contactResults = ref([]);
const contactSearching = ref(false);
const selectedContacts = ref([]);

const agentOptions = computed(() => store.getters['agents/getAgents'] || []);

const selectedContactIds = computed(() =>
  selectedContacts.value.map(c => c.id)
);

const availableContactResults = computed(() =>
  contactResults.value.filter(c => !selectedContactIds.value.includes(c.id))
);

const toDate = timestamp => {
  if (!timestamp) {
    return new Date();
  }

  return new Date(Number(timestamp) * 1000);
};

const resetState = () => {
  agentContactPair.value = false;
  useDateRange.value = Boolean(props.initialFrom && props.initialTo);
  dateRange.value = [toDate(props.initialFrom), toDate(props.initialTo)];
  selectedAgents.value = [];
  pairAgent.value = null;
  contactSearchQuery.value = '';
  contactResults.value = [];
  selectedContacts.value = [];
};

const open = () => {
  resetState();
  dialogRef.value?.open();
  requestAnimationFrame(() => {
    initialFocusRef.value?.focus();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const isSubmitDisabled = computed(() => {
  if (props.isSynthesizing) {
    return true;
  }

  if (agentContactPair.value) {
    if (!pairAgent.value?.id || selectedContacts.value.length === 0) {
      return true;
    }

    if (
      useDateRange.value &&
      (!dateRange.value?.[0] || !dateRange.value?.[1])
    ) {
      return true;
    }

    return false;
  }

  if (useDateRange.value) {
    if (!dateRange.value?.[0] || !dateRange.value?.[1]) {
      return true;
    }

    return false;
  }

  return selectedAgents.value.length === 0;
});

const getFilterPayload = () => {
  const base = {
    agentContactPair: agentContactPair.value,
    useDateRange: useDateRange.value,
    from: !useDateRange.value ? null : getUnixStartOfDay(dateRange.value[0]),
    to: !useDateRange.value ? null : getUnixEndOfDay(dateRange.value[1]),
  };

  if (agentContactPair.value) {
    return {
      ...base,
      agentIds: [pairAgent.value.id],
      contactIds: selectedContacts.value.map(c => c.id),
    };
  }

  return {
    ...base,
    agentIds: selectedAgents.value.map(a => a.id),
  };
};

const openQuestionsModal = () => {
  if (isSubmitDisabled.value) {
    return;
  }
  synthesisFilterSnapshot.value = getFilterPayload();
  isSynthesisOverlayOpen.value = true;
  synthesisDialogRef.value?.open();
};

const onSynthesisQuestionsConfirm = questions => {
  const base =
    synthesisFilterSnapshot.value != null
      ? synthesisFilterSnapshot.value
      : getFilterPayload();
  emit('synthesize', { ...base, questions });
};

const onSynthesisDialogClosed = () => {
  isSynthesisOverlayOpen.value = false;
  synthesisFilterSnapshot.value = null;
};

const onDateRangeChange = value => {
  dateRange.value = value;
};

const runContactSearch = debounce(async query => {
  const q = query?.trim() || '';
  if (q.length < 2) {
    contactResults.value = [];
    contactSearching.value = false;
    return;
  }

  contactSearching.value = true;
  try {
    contactResults.value = await searchContacts({
      keys: ['name', 'email', 'phone_number'],
      query: q,
    });
  } catch {
    contactResults.value = [];
  } finally {
    contactSearching.value = false;
  }
}, 300);

watch(contactSearchQuery, q => {
  runContactSearch(q);
});

const selectContact = contact => {
  if (selectedContactIds.value.includes(contact.id)) {
    return;
  }
  selectedContacts.value = [
    ...selectedContacts.value,
    { id: contact.id, name: contact.name },
  ];
  contactSearchQuery.value = '';
  contactResults.value = [];
};

const removeContact = contactId => {
  selectedContacts.value = selectedContacts.value.filter(
    c => c.id !== contactId
  );
};

// Carry the agent selection across mode changes so it is never silently dropped.
watch(agentContactPair, isPair => {
  if (isPair) {
    pairAgent.value =
      selectedAgents.value.length === 1 ? selectedAgents.value[0] : null;
    selectedAgents.value = [];
  } else {
    if (pairAgent.value) {
      selectedAgents.value = [pairAgent.value];
    }
    pairAgent.value = null;
    selectedContacts.value = [];
    contactSearchQuery.value = '';
    contactResults.value = [];
  }
});

onMounted(() => {
  store.dispatch('agents/get');
  resetState();
});

defineExpose({
  open,
  close,
  closeSynthesis: () => synthesisDialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :close-on-outside-click="!isSynthesisOverlayOpen"
    :show-confirm-button="false"
    :show-cancel-button="false"
    :cancel-button-label="t('DIALOG.BUTTONS.CANCEL')"
    :title="t('CSAT_SYNTHESIS.TITLE')"
    :description="t('CSAT_SYNTHESIS.DESCRIPTION')"
  >
    <div class="flex flex-col gap-5">
      <div ref="initialFocusRef" tabindex="-1" />

      <div class="rounded-xl border border-n-strong bg-n-alpha-2 p-4">
        <label class="flex items-start gap-3">
          <Checkbox v-model="agentContactPair" class="mt-1" />
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CSAT_SYNTHESIS.AGENT_CONTACT_PAIR') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CSAT_SYNTHESIS.AGENT_CONTACT_PAIR_HELP') }}
            </span>
          </div>
        </label>
      </div>

      <div v-if="agentContactPair" class="relative flex flex-col gap-4">
        <div class="relative z-40 flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CSAT_SYNTHESIS.PAIR_AGENT_LABEL') }}
          </span>
          <multiselect
            v-model="pairAgent"
            class="no-margin"
            :options="agentOptions"
            track-by="id"
            label="name"
            :multiple="false"
            close-on-select
            :clear-on-select="false"
            :placeholder="t('CSAT_SYNTHESIS.PAIR_AGENT_PLACEHOLDER')"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CSAT_SYNTHESIS.CONTACT_LABEL') }}
          </span>
          <div v-if="selectedContacts.length" class="flex flex-wrap gap-2">
            <span
              v-for="c in selectedContacts"
              :key="c.id"
              class="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-n-slate-3 bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12"
            >
              <span class="truncate">{{ c.name }}</span>
              <button
                type="button"
                class="shrink-0 rounded px-0.5 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :title="t('CSAT_SYNTHESIS.CONTACT_CLEAR')"
                @click="removeContact(c.id)"
              >
                <span class="i-lucide-x size-4" aria-hidden="true" />
                <span class="sr-only">
                  {{ t('CSAT_SYNTHESIS.CONTACT_CLEAR') }}
                </span>
              </button>
            </span>
          </div>
          <div class="relative z-20 flex flex-col gap-1">
            <input
              v-model="contactSearchQuery"
              type="search"
              autocomplete="off"
              class="reset-base h-10 block w-full rounded-lg border border-n-slate-5 bg-n-solid-1 px-3 py-2 text-sm !text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:border-n-brand"
              :placeholder="t('CSAT_SYNTHESIS.CONTACT_SEARCH_PLACEHOLDER')"
            />
            <div v-if="contactSearching" class="text-xs text-n-slate-11">
              {{ t('CSAT_SYNTHESIS.CONTACT_SEARCHING') }}
            </div>
            <ul
              v-else-if="
                contactSearchQuery.trim().length >= 2 &&
                availableContactResults.length
              "
              class="absolute left-0 right-0 top-full z-[100] mt-1 max-h-48 overflow-auto rounded-lg border border-n-weak bg-white py-1 shadow-xl dark:bg-n-solid-2"
            >
              <li v-for="c in availableContactResults" :key="c.id">
                <button
                  type="button"
                  class="flex w-full flex-col items-start px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                  @click="selectContact(c)"
                >
                  <span class="font-medium">{{ c.name }}</span>
                  <span
                    v-if="c.email || c.phoneNumber"
                    class="text-xs text-n-slate-11"
                  >
                    {{ c.email || c.phoneNumber }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
          <span class="text-sm text-n-slate-11">
            {{ t('CSAT_SYNTHESIS.CONTACT_HELP') }}
          </span>
        </div>
      </div>

      <div v-else class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CSAT_SYNTHESIS.AGENTS_LABEL') }}
        </span>
        <multiselect
          v-model="selectedAgents"
          class="no-margin"
          :options="agentOptions"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="t('CSAT_SYNTHESIS.AGENTS_PLACEHOLDER')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('CSAT_SYNTHESIS.AGENTS_HELP') }}
        </span>
      </div>

      <div
        class="relative flex flex-col gap-3"
        :class="isDatePickerOpen ? 'z-50' : 'z-0'"
      >
        <label class="flex items-start gap-3">
          <Checkbox v-model="useDateRange" class="mt-1" />
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CSAT_SYNTHESIS.USE_DATE_RANGE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('CSAT_SYNTHESIS.USE_DATE_RANGE_HELP') }}
            </span>
          </div>
        </label>

        <WootDateRangePicker
          v-if="useDateRange"
          :append-to-body="false"
          show-range
          class="no-margin auto-width"
          :value="dateRange"
          :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
          :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
          @change="onDateRangeChange"
          @open="isDatePickerOpen = true"
          @close="isDatePickerOpen = false"
        />
      </div>
    </div>

    <template #footer>
      <div
        class="relative z-10 flex w-full flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          class="w-full sm:w-auto"
          type="button"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          :disabled="isSynthesizing"
          @click="close"
        />
        <Button
          color="blue"
          class="w-full sm:w-auto"
          type="button"
          icon="i-ph-sparkle"
          :label="t('CSAT_SYNTHESIS.SYNTHESIS_BUTTON')"
          :disabled="isSubmitDisabled"
          @click="openQuestionsModal"
        />
      </div>
    </template>
  </Dialog>

  <ConversationTranscriptsSynthesisDialog
    ref="synthesisDialogRef"
    :is-loading="isSynthesizing"
    i18n-prefix="CSAT_SYNTHESIS.QUESTIONS"
    @confirm="onSynthesisQuestionsConfirm"
    @close="onSynthesisDialogClosed"
  />
</template>

<style scoped>
:deep(.multiselect__content-wrapper) {
  @apply bg-n-surface-1 border border-n-weak text-n-slate-12 rounded-b-lg;
  backdrop-filter: none !important;
}

:deep(.multiselect__content) {
  @apply bg-n-surface-1;
}

:deep(.multiselect__option) {
  @apply bg-n-surface-1 text-n-slate-12;
}

:deep(.multiselect__option--highlight) {
  @apply bg-n-alpha-2 text-n-slate-12;
}

:deep(.multiselect__tags) {
  @apply bg-n-surface-1 border border-n-weak m-0 min-h-[2.875rem] pt-0 rounded-lg;
}

:deep(.multiselect__tags input[type='text']) {
  @apply bg-transparent border-n-weak placeholder:text-n-slate-11 min-h-[2.375rem] h-[2.375rem] px-0.5 py-0 text-sm;
}

:deep(.multiselect__placeholder) {
  @apply text-n-slate-11;
}

:deep(.multiselect__input) {
  @apply text-sm h-[2.375rem] mb-0 py-0 bg-transparent;
}

:deep(.multiselect__tags),
:deep(.multiselect__input),
:deep(.multiselect) {
  @apply border-n-weak text-n-slate-12 rounded-lg text-sm min-h-[2.5rem];
}

:deep(.mx-input-wrapper) {
  @apply bg-n-surface-1 border border-n-weak text-n-slate-12 rounded-lg text-sm;
}

:deep(.mx-input-wrapper input) {
  @apply border-n-weak text-sm text-n-slate-12 bg-transparent;
}

:deep(.mx-datepicker .mx-input) {
  background-color: transparent !important;
}
</style>
